import jsPDF from "jspdf";
import axios from "axios";

import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${window.location.origin}/pdf.worker.min.js`;


let skuImage = "";
let tableData = [];
const baseURL = sessionStorage.getItem('baseURL')

let snappedPdfImage = null;

/* ==================== LAYOUT CONSTANTS ==================== */

const PAGE_HEIGHT = 297;
const TOP_MARGIN = 20;
const FOOTER_Y = 287;
const BODY_MAX_Y = 240;
// const SIGNATURE_Y = 255;

/* ==================== HELPERS ==================== */

const fetchImageAsDataURL = async (imageUrl) => {
  try {
    console.log("Fetching image from URL:", imageUrl);
    const response = await axios.get(imageUrl, { responseType: "arraybuffer" });
    const blob = new Blob([response.data], { type: response.headers["content-type"] });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error fetching and converting image:", error);
    throw error;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true
  }).format(date);
};

function formatMonthYear(date) {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString("default", { month: "long", year: "numeric" });
}

/* ==================== DATA FETCH ==================== */

const snapPdfSection = async ({
  pdfUrl,
  pageNumber = 1,
  scale = 2
}) => {
  const res = await fetch(pdfUrl);
  const buffer = await res.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const page = await pdf.getPage(pageNumber);

  const viewport = page.getViewport({ scale });

  const cmToPx = (cm) => (cm / 2.54) * 96 * scale;
  const snapSize = cmToPx(3); // 3cm × 3cm

  // 🔹 CENTER CALCULATION
  const x = viewport.width / 2 - snapSize / 2;
  const y = viewport.height / 2 - snapSize / 2;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = snapSize;
  cropCanvas.height = snapSize;

  cropCanvas
    .getContext("2d")
    .drawImage(
      canvas,
      x,
      y,
      snapSize,
      snapSize,
      0,
      0,
      snapSize,
      snapSize
    );

  return cropCanvas.toDataURL("image/png");
};

async function fetchAllBatchData(sku, qr = null) {
  try {
    console.log(baseURL + "sku/SKUImage/" + sku.product_image.replaceAll("/", "-"));
    skuImage = await fetchImageAsDataURL(
      baseURL + "sku/SKUImage/" +
      sku.product_image.replaceAll("/", "-")
    );

    let qrRes = [];
    if (!qr) {
      qrRes = await axios.get(
        baseURL + "qr/fetchQR/" + sku.sku_id
      );
      tableData = qrRes.data;
    }
    else {
      qrRes = qr
      tableData = [qrRes]
      const PDF_SNAP_CONFIG = {
        pdfUrl: baseURL + "qr/fetchQRDoc/" + qr.qr_id + ".pdf/" + sku.sku_id,
        pageNumber: 1,
        scale: 2
      };
      try {
        snappedPdfImage = await snapPdfSection(PDF_SNAP_CONFIG);
      } catch (err) {
        console.warn("PDF section snap failed:", err);
      }
    }

    for (let i = 0; i < tableData.length; i++) {
      const qrRow = tableData[i];

      const batchRes = await axios.get(
        baseURL + "batch/fetchBatchDetails/" +
        qrRow.qr_id +
        "/" +
        sku.sku_id
      );

      const batchData = batchRes.data;
      tableData[i].batch = batchData;
    }
  } catch (e) {
    console.error("Error fetching batch data:", e);
    throw e;
  }
}

/* ==================== MAIN PDF ==================== */

export async function downloadSkuReport(sku, details, batchReqd = false, qr = null) {
  const doc = new jsPDF();
  let yPos = TOP_MARGIN;

  /* ---------------- HEADER ---------------- */

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 35, "F");

  try {
    doc.addImage("logo.png", "PNG", 10, 5, 30, 25);
  } catch { }

  try {
    doc.addImage("QRmor.jpeg", "PNG", 170, 5, 30, 25);
  } catch { }

  doc.setTextColor(0);
  doc.setFontSize(22);
  doc.text("SKU INFORMATION REPORT", 105, 15, { align: "center" });

  doc.setFontSize(12);
  doc.text(
    `Generated: ${new Date().toLocaleString()} | ${sessionStorage.getItem("name")}`,
    105,
    25,
    { align: "center" }
  );

  doc.setTextColor(0);

  /* ---------------- PRODUCT DETAILS ---------------- */

  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Product Details", 20, 50);

  try {
    if (skuImage) {
      doc.addImage(skuImage, "JPEG", 140, 55, 50, 50);
    }
  } catch { }

  doc.setFontSize(11);
  doc.setFont(undefined, "normal");

  yPos = 60;

  details.forEach((d) => {
    if (yPos + 10 > BODY_MAX_Y) {
      doc.addPage();
      yPos = TOP_MARGIN;
    }

    doc.setFont(undefined, "bold");
    doc.text(d.label, 20, yPos);

    doc.setFont(undefined, "normal");

    const valueText = String(d.value || "-");
    const wrappedText = doc.splitTextToSize(valueText, 70);

    wrappedText.forEach((line, index) => {
      doc.text(line, 70, yPos + index * 6);
    });

    yPos += wrappedText.length * 6 + 4;
  });

  if (yPos < 105) yPos = 105;

  /* ---------------- QR TABLE HEADER ---------------- */

  yPos += 5;
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  if (!qr) {
    doc.text("QR & Batch Details", 20, yPos);

    yPos += 12;
  }
  else {
    doc.text("Batch Details", 20, yPos);
    yPos += 9;
    doc.setFontSize(11)
    doc.setFont(undefined, "normal");
    doc.text("Total Batches: " + qr.batch_count, 20, yPos);
    yPos += 12;

    const getBase64FromUrl = async (url) => {
      const res = await fetch(url);
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    };

    try {
      const img = await getBase64FromUrl(skuImage);
      doc.addImage(img, 'JPEG', 140, 55, 50, 50);

      if (snappedPdfImage) {
        doc.setDrawColor(180);
        doc.rect(152, 110, 30, 30); // subtle border

        doc.addImage(
          snappedPdfImage,
          "PNG",
          153,
          111,
          28,
          28
        );

        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text("Sample QRmor Code Snapshot", 142, 144);
        doc.setTextColor(0);
      }
    } catch {
      /* image optional */
    }
  }

  /* ======================= DATA RENDER ======================= */

  tableData.forEach((qrRow) => {
    const qrId = qrRow.uid;
    const generatedAt = formatDate(qrRow.timestamp);
    const batches = Array.isArray(qrRow.batch) ? qrRow.batch : [];

    if (yPos + 12 > BODY_MAX_Y) {
      doc.addPage();
      yPos = TOP_MARGIN;
    }

    /* ================= QR HEADER ================= */

    const qrHeaderHeight = 10;

    if (!qr) {
      doc.setFillColor(220, 230, 241);
      doc.rect(10, yPos - 7, 190, qrHeaderHeight, "F");

      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text(`QR ID: ${qrId}`, 15, yPos);
      doc.text(`Batches: ${batches.length}`, 70, yPos);
      doc.text(`${generatedAt}`, 190, yPos, { align: "right" });

      yPos += qrHeaderHeight + 4;
    }

    if (batchReqd) {
      batches.forEach((batch, batchIndex) => {
        const batchHeaderHeight = 8;

        if (yPos + batchHeaderHeight > BODY_MAX_Y) {
          doc.addPage();
          yPos = TOP_MARGIN;
        }

        const batchStartY = yPos;
        const leftX = 10;
        const width = 190;

        doc.setFillColor(255, 255, 255);
        doc.rect(leftX, batchStartY - 6, width, 60, "F");

        doc.setFont(undefined, "bold");
        doc.setFontSize(11);

        doc.text(
          `${batchIndex + 1}. Batch ID: ${batch.batch_no}`,
          15,
          yPos
        );

        doc.text(
          `   Date of Manufacturing: ${formatMonthYear(batch.dom)}`,
          60,
          yPos
        );

        doc.text(
          `Date of Expiry: ${formatMonthYear(batch.doe)}`,
          145,
          yPos
        );

        yPos += batchHeaderHeight;

        doc.setFont(undefined, "normal");
        doc.setFontSize(10);

        let statusText =
          batch.recalled
            ? "Recalled"
            : batch.status === 1
              ? "Approved"
              : batch.status === 0
                ? "Pending"
                : "Rejected";

        doc.text(`Status: ${statusText}`, 18, yPos);
        yPos += 6;

        const remarks = Array.isArray(batch.remarks)
          ? batch.remarks
          : [];

        remarks.forEach((remark, remarkIndex) => {
          const prefix = String.fromCharCode(97 + remarkIndex);
          const remarkText = `${prefix}. ${remark.message || ""}`;
          const remarkTime = `${formatDate(remark.timestamp) || ""}`;

          const lines = doc.splitTextToSize(remarkText, 160);
          const remarkHeight = lines.length * 5;

          if (yPos + remarkHeight > BODY_MAX_Y) {
            doc.addPage();
            yPos = TOP_MARGIN;
          }

          doc.text(lines, 20, yPos);
          yPos += remarkHeight;

          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text(`— ${remarkTime}`, 25, yPos);
          doc.setTextColor(0);
          doc.setFontSize(10);

          yPos += 6;
        });

        yPos -= 2;
        doc.setDrawColor(180);
        doc.line(10, yPos, 200, yPos);
        yPos += 6;
      });
    }
  });

  /* ---------------- FOOTER ---------------- */

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130);

    doc.text(`Page ${i} of ${pageCount}`, 105, FOOTER_Y + 5, {
      align: "center"
    });

    doc.text("Confidential – Internal Use Only", 105, FOOTER_Y, {
      align: "center"
    });

    doc.setFontSize(9);
    doc.setTextColor(100);

    doc.text("QRmor Asset Security Analytics Report", 140, FOOTER_Y - 5);
    doc.text("© 2026 Scinova Scientifics", 150, FOOTER_Y);
    doc.text("Powered by CIIRC®", 155, FOOTER_Y + 5);
  }

  /* ---------------- SIGNATURE ---------------- */
  if (yPos > PAGE_HEIGHT - 38) {
    doc.addPage();
    yPos = TOP_MARGIN;
  }

  doc.setFontSize(10);
  doc.setTextColor(0);

  doc.text("End of Report", 105, yPos + 15, { align: "center" });
  doc.text("__________________________", 150, yPos + 30);
  doc.text("Authorized Signatory", 155, yPos + 38);

  /* ---------------- SAVE ---------------- */

  let fileName = batchReqd
    ? `SKU_${sku[0]}_DetailedReport-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`
    : `SKU_${sku[0]}_Report-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;

  const pdfBlob = doc.output("blob");
  const pdfFile = new File([pdfBlob], fileName, {
    type: "application/pdf"
  });

  const pdfUrl = URL.createObjectURL(pdfFile);
  window.open(pdfUrl, "_blank");
}

/* ==================== ENTRY ==================== */

export async function skuReportPdf(sku, batchReqd = false, qr = null) {
  let details = []
  if (qr) {
    details.push({ label: "QR UID:", value: qr.uid });
  }
  let dets = [
    { label: "Product Name:", value: sku.brand_name },
    { label: "GTIN:", value: sku.gtin },
    { label: "Generic Name:", value: sku.generic_name },
    { label: "Manufacturing Lic. No.:", value: sku.manufacturing_license },
    {
      label: "Production Unit:",
      value: sku.prod_unit + "\n" + sku.address
    },
    { label: "List of Excipients:", value: sku.excipients}
  ];
  for (const [index, item] of dets.entries()) {
    details.push(item);
  }

  await fetchAllBatchData(sku, qr);
  await downloadSkuReport(sku, details, batchReqd, qr);
}
